import {
  appLogout,
  injectSlice,
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
  State,
} from '@bonanza-social/toolkit/state';
import { InboxMessageResponse, InboxState } from './inbox.types';
import InboxService from '../services/InboxService/InboxService';
import AccountService from '../services/accountService/accountService';

const initialState: InboxState = {
  data: undefined,
  dataState: 'idle',
  readMessages: [],
  marketingPreferences: {
    data: undefined,
    dataState: 'idle',
  },
};

export const fetchInbox = createAsyncThunk('inbox/fetch', async () => {
  const messages = await InboxService.getInbox();

  return messages;
});

export const acknowledgeInbox = createAsyncThunk(
  'inbox/acknowledgeInbox',
  (_, { getState, dispatch }) => {
    const readMessages = selectReadMessages(getState() as State);

    if (!readMessages.length) {
      return;
    }

    dispatch(markInboxAsRead(readMessages));

    InboxService.acknowledgeInbox({
      data: { notificationIds: readMessages, isRead: true },
    });
  },
);

export const SmarticoNotificationClicked = createAsyncThunk(
  'inbox/SmarticoNotificationClicked',
  (id: number, { dispatch }) => {
    if (!id) {
      return;
    }

    dispatch(markMarketingMessageAsClicked(id));

    InboxService.SmarticoNotificationClicked({ pathParams: { id } });
  },
);

export const getMarketingPreferences = createAsyncThunk(
  'inbox/getMarketingPreferences',
  async () => {
    const marketingPreferences = await AccountService.getAccountMarketing();

    return marketingPreferences;
  },
);

const inbox = createSlice({
  name: 'status',
  initialState,
  reducers: {
    addInboxMessage(state, action: PayloadAction<InboxMessageResponse>) {
      state.data?.unshift(action.payload);
    },
    markInboxAsRead(state, action: PayloadAction<number[]>) {
      state.data?.forEach((message) => {
        if (action.payload.includes(message.id)) {
          message.isRead = true;
        }
      });
    },
    markMarketingMessageAsClicked(state, action: PayloadAction<number>) {
      const message = state.data?.find((msg) => msg.id === action.payload);
      if (message) {
        message.cta.isClicked = true;
      }
    },
    updateReadMessagesList(state, action: PayloadAction<number[]>) {
      state.readMessages = [
        ...new Set([...state.readMessages, ...action.payload]),
      ];
    },
    removeMarketingMessages(state) {
      state.data = state.data?.filter(
        (message) => message.category !== 'Marketing',
      );
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchInbox.pending, (state) => {
      state.dataState =
        state.dataState === 'fulfilled' ? 'refreshing' : 'pending';
    });

    builder.addCase(fetchInbox.rejected, (state) => {
      state.dataState = 'rejected';
    });

    builder.addCase(fetchInbox.fulfilled, (state, action) => {
      state.data = action.payload;
      state.dataState = 'fulfilled';
    });

    builder.addCase(appLogout, (state) => {
      Object.assign(state, initialState);
    });

    builder.addCase(getMarketingPreferences.pending, (state) => {
      state.marketingPreferences.dataState = 'pending';
    });

    builder.addCase(getMarketingPreferences.rejected, (state) => {
      state.marketingPreferences.dataState = 'rejected';
    });

    builder.addCase(getMarketingPreferences.fulfilled, (state, action) => {
      state.marketingPreferences.data =
        action.payload.marketingPreferences?.allowInboxMarketingTool;
      state.marketingPreferences.dataState = 'fulfilled';
    });
  },
});

export const {
  addInboxMessage,
  markInboxAsRead,
  updateReadMessagesList,
  markMarketingMessageAsClicked,
  removeMarketingMessages,
} = inbox.actions;

export const withInbox = injectSlice({
  reducerPath: 'inbox',
  reducer: inbox.reducer,
});

export const selectInbox = withInbox.selector((state) => state.inbox.data);
export const selectUnreadMessages = createSelector(selectInbox, (data) =>
  data?.filter((message) => !message.isRead),
);

export const selectMessagesByCategory = createSelector(
  [selectInbox, (_, category?: string) => category],
  (data, category) => {
    if (!category) return data;
    return data?.filter((message) => message.category === category);
  },
);

export const selectInboxDataState = withInbox.selector(
  (state) => state.inbox.dataState,
);

export const selectHasUnreadInboxMessages = createSelector(
  selectInbox,
  (data) => data?.some((item) => !item.isRead),
);

export const selectReadMessages = withInbox.selector(
  (state) => state.inbox.readMessages,
);

export const selectMarketingPreferences = withInbox.selector(
  (state) => state.inbox.marketingPreferences.data,
);

export const selectMarketingPreferencesDataState = withInbox.selector(
  (state) => state.inbox.marketingPreferences.dataState,
);

declare global {
  interface LazyLoadedSlices {
    inbox: ReturnType<typeof inbox.reducer>;
  }
}
